import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useLanguage } from '../contexts/LanguageContext'

const POPUP_MEDIA = typeof window !== 'undefined' ? window.matchMedia('(max-width: 900px)') : null

export function LanguageSelector({ compact = false, theme }) {
  const { language, setLanguage, t, supportedLanguages } = useLanguage()
  const [open, setOpen] = useState(false)
  const [usePopup, setUsePopup] = useState(() => POPUP_MEDIA?.matches ?? false)
  const ref = useRef(null)

  useEffect(() => {
    if (!POPUP_MEDIA) return
    const handler = () => setUsePopup(POPUP_MEDIA.matches)
    POPUP_MEDIA.addEventListener('change', handler)
    return () => POPUP_MEDIA.removeEventListener('change', handler)
  }, [])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  const current = supportedLanguages.find((l) => l.code === language) ?? supportedLanguages[0]

  const dropdownContent = open && (
    <>
      {compact && (
        <div
          className="language-selector__popup-backdrop"
          aria-hidden="true"
          onClick={() => setOpen(false)}
        />
      )}
      <ul
        className={`language-selector__dropdown ${compact ? 'language-selector__dropdown--popup' : ''} ${theme === 'dark' ? 'language-selector__dropdown--dark' : ''}`}
        role="listbox"
        aria-label="Language options"
      >
        {supportedLanguages.map((opt) => (
          <li key={opt.code} role="option" aria-selected={language === opt.code}>
            <button
              type="button"
              className={`language-selector__option ${language === opt.code ? 'is-active' : ''}`}
              onClick={() => {
                const code = opt.code
                console.debug('[LanguageSelector] Option clicked', { code, currentLanguage: language })
                setOpen(false)
                setTimeout(() => {
                  console.debug('[LanguageSelector] Calling setLanguage', code)
                  setLanguage(code)
                }, 0)
              }}
            >
              <span className="language-selector__option-flag">{opt.flag}</span>
              <span>{t(opt.labelKey)}</span>
            </button>
          </li>
        ))}
      </ul>
    </>
  )

  return (
    <div className={`language-selector ${compact ? 'language-selector--compact' : ''} ${open ? 'language-selector--open' : ''}`} ref={ref}>
      <button
        type="button"
        className="language-selector__trigger"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="Select language"
      >
        <span className="language-selector__flag" aria-hidden="true">{current.flag}</span>
        {!compact && (
          <>
            <span className="language-selector__code">{current.code.toUpperCase()}</span>
            <svg className="language-selector__chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </>
        )}
      </button>
      {compact && usePopup && open && typeof document !== 'undefined' && document.body
        ? createPortal(dropdownContent, document.body)
        : dropdownContent}
    </div>
  )
}
